<script setup>
    // Vue
    import { ref, computed, inject, getCurrentInstance } from 'vue';
    import { onMounted } from 'vue';
    import { useRouter } from 'vue-router';

    // Compoent
    import agreementDetail from '@/components/modal/AgreementDetailComponent.vue';
    import agreementList from '@/components/modal/AgreementListComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';
    import userDataUpdate from '@/components/modal/UserDataUpdateComponent.vue';
    import userLoginPasswordUpdate from '@/components/modal/UserLoginPasswordUpdateComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useUserStore } from '@/stores/user/userStore';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // APIのドメイン部を管理する。
    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 辞書利用
    const commonStore = useCommonStore();

    // 言語情報取得
    const languageStore = useLanguageStore();

    // ユーザー情報取得
    const userStore = useUserStore();

    const router = useRouter();

    // ユーザー情報更新モーダル
    const userDataUpdateDispFlg = ref('');
    const userDataUpdateComponent = ref();

    // ログインしているユーザーの認証方法が、1：カウンセリング、2：タフネスかを判定し、
    // 1以外の場合は、パスワード変更エリアを非表示にする。
    // また、タフネス連携ユーザーでログインしているか判定する。
    // タフネス連携ユーザーの場合は、
    // ・パスワード変更エリアを非表示にする。
    const isPasswordDisp = ref(false);

    function userDataUpdateOpen() {
        userDataUpdateComponent.value.openModal(API_URL);
        userDataUpdateDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }
    function userDataUpdateClose() {
        userDataUpdateDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }
    async function userDataUpdateUpdate() {
        // ユーザー情報更新モーダルを閉じる。
        userDataUpdateDispFlg.value = '';

        document.body.classList.remove('modal-open'); //スクロール再開

        // 初期化する。
        reset();

        // ユーザー情報取得
        await getUser();
    }

    // パスワード変更モーダル
    const userLoginPasswordUpdateDispFlg = ref('');
    const userLoginPasswordUpdateComponent = ref();
    function userLoginPasswordUpdateOpen() {
        userLoginPasswordUpdateComponent.value.openModal(API_URL);
        userLoginPasswordUpdateDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }
    function userLoginPasswordUpdateClose() {
        userLoginPasswordUpdateDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }
    function userLoginPasswordUpdateUpdate() {
        userLoginPasswordUpdateDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    // 同意済み規定一覧モーダル
    const agreementListDispFlg = ref('');
    const agreementListComponent = ref();
    function agreementListOpen() {
        agreementListComponent.value.openModal(API_URL, languageStore.getLanguageValue);
        agreementListDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }
    function agreementListClose() {
        agreementListDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    // 規定詳細モーダル
    const agreementDetailDispFlg = ref('');
    const agreementDetailComponent = ref();
    function agreementDetailOpen(regulationPk, regulationHistPk) {
        // 同意済み規定一覧モーダルを閉じる
        agreementListDispFlg.value = '';

        // 同意済み規定詳細モーダルを表示する
        agreementDetailComponent.value.openModal(API_URL, languageStore.getLanguageValue, regulationPk, regulationHistPk);
        agreementDetailDispFlg.value = 'block';
    }
    function agreementDetailClose() {
        // 同意済み規定一覧モーダルを再表示
        agreementListComponent.value.openModal(API_URL, languageStore.getLanguageValue);
        agreementListDispFlg.value = 'block';

        // 同意済み規定詳細モーダルを閉じる
        agreementDetailDispFlg.value = '';
    }

    /**
     * ユーザ情報取得処理
     * @param queryParam
     * @returns true:正常 false:異常
     */

    async function getUserInfo() {
        const response = await userStore.getUserAction(API_URL, null);
        if (response.success) {
            if (!utils.isNullEmpty(userStore.userModel.AuthenticateMethod) && userStore.userModel.AuthenticateMethod === '1') {
                isPasswordDisp.value = true;
            }
            return true;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
    }

    /**
     * ユーザー情報詳細取得処理
     * @param queryParam
     * @returns なし
     */
    async function getUserDetail() {
        const response = await userStore.getUserDetailAction(API_URL, null);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * ユーザー情報取得処理
     */
    async function getUser() {
        // ユーザー情報取得
        const result = await getUserInfo();

        if (!result) {
            return;
        }

        // ユーザー詳細情報取得
        await getUserDetail();
    }

    const NameKj = computed(() => {
        // 氏名の漢字はユーザー情報取得、ユーザー情報詳細取得双方から取得できる。
        return utils.changeNullValueToEmpty(userStore.userDetail.NameKjLs) + ' ' + utils.changeNullValueToEmpty(userStore.userModel.NameKjFs);
    });

    const NameKn = computed(() => {
        // 氏名のカナはユーザー情報詳細取得から取得できる。
        return utils.changeNullValueToEmpty(userStore.userDetail.NameKnLs) + ' ' + utils.changeNullValueToEmpty(userStore.userDetail.NameKnFs);
    });

    async function backToHome() {
        router.push('/home');
    }

    onMounted(async () => {
        // APIのURLを引き継ぐ。
        await commonStore.getDomain();

        // 初期化する。
        reset();

        // ユーザー情報取得
        await getUser();

        // SSOでログインしているかを判定するフラグを取得する。
        if (commonStore.isSsoLoggedin) {
            isPasswordDisp.value = false;
        }
    });

    /**
     * リセットする。
     */
    function reset() {
        userStore.reset();
    }
</script>

<style></style>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: userDataUpdateDispFlg }">
            <!--ご登録情報更新モーダル-->
            <userDataUpdate ref="userDataUpdateComponent" @userDataUpdateModal:cancel="userDataUpdateClose" @userDataUpdateModal:update="userDataUpdateUpdate" />
        </section>
        <section id="overlay" :style="{ display: userLoginPasswordUpdateDispFlg }">
            <!--パスワード変更モーダル-->
            <userLoginPasswordUpdate ref="userLoginPasswordUpdateComponent" @userLoginPasswordUpdateModal:cancel="userLoginPasswordUpdateClose" @userLoginPasswordUpdateModal:update="userLoginPasswordUpdateUpdate" />
        </section>
        <section id="overlay" :style="{ display: agreementListDispFlg }">
            <!--同意済み規定一覧モーダル-->
            <agreementList ref="agreementListComponent" @agreementListModal:open="agreementDetailOpen" @agreementListModal:close="agreementListClose" />
        </section>
        <section id="overlay" :style="{ display: agreementDetailDispFlg }">
            <!--同意済み規定詳細モーダル-->
            <agreementDetail ref="agreementDetailComponent" @agreementDetailModal:close="agreementDetailClose" />
        </section>
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <span>{{ $t('userDetail.parts.modalTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents userDetail scroll">
                    <section class="form scroll">
                        <div class="formBox">
                            <!--ユーザー情報の詳細情報-->
                            <dl class="line">
                                <!--企業ID-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl12') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userModel.CompanyId) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご契約企業-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl1') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userModel.CompanyNm) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ご契約企業との関係-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl2') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ commonStore.getConvertDictionary('ClientType', userStore.userModel.ClientType, languageStore.getLanguageValue) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--ユーザーID-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl13') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userModel.UserId) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--氏名（漢字）-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl3') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ NameKj }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--氏名（カナ）-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl4') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ NameKn }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--メールアドレス-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl5') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userDetail.MailAddress) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--生年月日-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl6') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userDetail.Birthday) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--性別-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl7') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ commonStore.getConvertDictionary('Gender', userStore.userDetail.Gender, languageStore.getLanguageValue) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--言語-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl8') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ commonStore.getConvertDictionary('Lang', userStore.userDetail.Lang, languageStore.getLanguageValue) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl class="line">
                                <!--電話番号-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl9') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <span>{{ utils.changeNullValueToEmpty(userStore.userModel.PhoneNumber) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl id="userKeyNumberName" class="line" style="margin-bottom: 0px">
                                <!--ユーザーキー情報-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label :v-if="userStore.userModel.UserKeyNoSupport">{{ utils.changeNullValueToEmpty(userStore.userModel.UserKeyNoNm) }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li class="marginRightAuto">
                                            <span :v-if="userStore.userModel.UserKeyNoSupport">{{ utils.changeNullValueToEmpty(userStore.userDetail.UserKeyNo) }}</span>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <ul class="btnArea right">
                                <li>
                                    <!--ご登録情報更新-->
                                    <button type="button" class="btnPrimary" @click="userDataUpdateOpen">{{ $t('userDetail.parts.button1') }}</button>
                                </li>
                            </ul>
                        </div>
                        <template v-if="isPasswordDisp">
                            <div class="formBox" style="padding-bottom: 7px">
                                <!--パスワードの変更画面-->
                                <dl>
                                    <dt>
                                        <ul class="formLabel">
                                            <li>
                                                <label>{{ $t('userDetail.parts.dl10') }}</label>
                                            </li>
                                        </ul>
                                    </dt>
                                    <dd>
                                        <ul class="formInput">
                                            <li style="margin-right: auto" />
                                            <li>
                                                <!--パスワード変更-->
                                                <button type="button" class="btnPrimary" @click="userLoginPasswordUpdateOpen">{{ $t('userDetail.parts.button2') }}</button>
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                            </div>
                        </template>
                        <div class="formBox" style="padding-bottom: 7px">
                            <!--規定の詳細情報-->
                            <dl>
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('userDetail.parts.dl11') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li style="margin-right: auto" />
                                        <li>
                                            <!--同意済みの規定一覧-->
                                            <button type="button" class="btnPrimary" @click="agreementListOpen">{{ $t('userDetail.parts.button3') }}</button>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </div>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" class="btnNormal" @click="backToHome">{{ $t('common.parts.backPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>
